import { Injectable, Optional } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { map, mergeMap, take, tap } from 'rxjs/operators';
import { Observable, of } from 'rxjs';
import { CurrentDataService } from './current-data-service';
import { ColorsTransporterConfigurationService } from './colors-transporter-configuration.service';
import { TransporterConfigurationService } from './transporter-configuration.service';
import { ThemeExtendedService } from './theme-extended.service';
import { MetadataService } from './metadata.service';
import { Transporter } from '../models/transporter.model';
import { BaryColorService } from './bary-color.service';
import { TransporterConfigurationEnum } from '../models/enums/configuration.enum';
import { ThemeView } from '../models/theme-view.model';
import { environment } from '../environments/environment';

@Injectable({
  providedIn: 'root',
})
export class CurrentTransporterService {
  public resourceUrl = environment.urlApi + '/api/transporter/current';

  constructor(
    protected http: HttpClient,
    private currentDataService: CurrentDataService,
    private colorsTransporterConfigurationService: ColorsTransporterConfigurationService,
    private transporterConfigurationService: TransporterConfigurationService,
    private themeService: ThemeExtendedService,
    @Optional() private metadataService: MetadataService
  ) {}

  async initTransporter(): Promise<any> {
    await this.http
      .get<Transporter>(`${this.resourceUrl}`, { observe: 'response' })
      .pipe(
        take(1),
        map(currentTransporterHttpResponse => this.currentDataService.updateTransporter(currentTransporterHttpResponse.body!)),
        mergeMap(() => this.themeService.getCurrentTheme()),
        tap((theme: ThemeView) => this.currentDataService.updateCurrentTheme(theme)),
        mergeMap(() => this.setTransporterColors()),
        mergeMap(() => this.setTransporterScript()),
        mergeMap(() => this.currentDataService.getCurrentTransporter()),
        mergeMap((transporter?: Transporter) => {
          if (this.metadataService && transporter) {
            return this.metadataService.updateMetadataForTransporter(transporter);
          }
          return of();
        })
      )
      .toPromise();
  }

  public setTransporterColors(): Observable<void> {
    return this.colorsTransporterConfigurationService.retrieveConfiguration().pipe(
      take(1),
      map(value => {
        const color = value.body;
        BaryColorService.exportColor('--transporter-primary', color!.primary);
        BaryColorService.exportColor('--transporter-secondary', color!.secondary);
      })
    );
  }

  public setTransporterScript(): Observable<void> {
    return this.transporterConfigurationService.getStringConfiguration(TransporterConfigurationEnum.GET_TRANSPORTER_SCRIPT).pipe(
      take(1),
      map((data: any): void => {
        if (data.value) {
          const node = document.createElement('script');
          node.src = data.value;
          node.type = 'text/javascript';
          node.async = true;
          document.getElementsByTagName('head')[0].appendChild(node);
        }
      })
    );
  }
}
